import { AgentView, ChatView, PromptView } from '@lemoncloud/ssocio-chatbots-api';
import { QueryClient } from '@tanstack/react-query';

import { updateAllListCaches } from '@codes/shared';
export { updateAllListCaches };

export const updateCacheWithNewChat = (queryClient: QueryClient, resource: string, chatView: ChatView) => {
    updateAllListCaches({ resource, queryClient }, (oldData: any) => {
        if (!oldData?.data) {
            return oldData;
        }

        // ID 중복 체크
        const isDuplicate = oldData.data.some((item: ChatView) => item.id === chatView.id);
        if (isDuplicate) {
            return oldData;
        }

        return {
            ...oldData,
            data: [chatView, ...oldData.data],
            list: [chatView, ...oldData.list],
            total: (oldData.total || 0) + 1,
        };
    });
};

export const updateCacheWithNewAgent = (queryClient: QueryClient, resource: string, newAgent: AgentView) => {
    updateAllListCaches({ resource, queryClient }, (oldData: any) => {
        if (!oldData?.data) {
            return oldData;
        }

        // ID 중복 체크
        const isDuplicate = oldData.data.some((item: AgentView) => item.id === newAgent.id);
        if (isDuplicate) {
            return oldData;
        }

        return {
            ...oldData,
            data: [newAgent, ...oldData.data],
            list: [newAgent, ...oldData.list],
            total: (oldData.total || 0) + 1,
        };
    });
};

export const updateCacheWithUpdatedAgent = (
    queryClient: QueryClient,
    resource: string,
    updatedAgent: AgentView,
    agentId: string
) => {
    updateAllListCaches({ resource, queryClient, shouldInvalidate: false }, (oldData: any) => {
        if (!oldData?.data) {
            return oldData;
        }

        const updateAgent = (origin: AgentView) => (origin.id === agentId ? updatedAgent : origin);
        return {
            ...oldData,
            data: oldData.data.map(updateAgent),
            list: oldData.list.map(updateAgent),
        };
    });
};

export const updateCacheWithNewPrompt = (queryClient: QueryClient, resource: string, promptView: PromptView) => {
    updateAllListCaches({ resource, queryClient }, (oldData: any) => {
        if (!oldData?.data) {
            return oldData;
        }

        // ID 중복 체크
        const isDuplicate = oldData.data.some((item: PromptView) => item.id === promptView.id);
        if (isDuplicate) {
            return oldData;
        }

        return {
            ...oldData,
            data: [promptView, ...oldData.data],
            list: [promptView, ...oldData.list],
            total: (oldData.total || 0) + 1,
        };
    });
};

export const updateCacheWithUpdatedPrompt = (
    queryClient: QueryClient,
    resource: string,
    updatedPrompt: PromptView,
    promptId: string
) => {
    updateAllListCaches({ resource, queryClient, shouldInvalidate: false }, (oldData: any) => {
        if (!oldData?.data) {
            return oldData;
        }

        const updatePrompt = (origin: PromptView) => (origin.id === promptId ? updatedPrompt : origin);
        return {
            ...oldData,
            data: oldData.data.map(updatePrompt),
            list: oldData.list.map(updatePrompt),
        };
    });
};

/**
 * 프롬프트가 업데이트될 때 해당 프롬프트를 사용하는 에이전트들의 캐시도 업데이트
 */
export const updateAgentCachesForPromptUpdate = (
    queryClient: QueryClient,
    updatedPrompt: PromptView,
    promptId: string
) => {
    // 에이전트 목록 캐시에서 해당 프롬프트를 사용하는 에이전트들을 찾아 업데이트
    updateAllListCaches({ resource: 'agents', queryClient, shouldInvalidate: false }, (oldData: any) => {
        if (!oldData?.data) {
            return oldData;
        }

        const updateAgentIfUsesPrompt = (agent: AgentView) => {
            // 에이전트가 해당 프롬프트를 사용하는지 확인
            const usesSystemPrompt = agent.promptId === promptId;
            const usesUserPrompt = agent.userPromptId === promptId;

            if (usesSystemPrompt || usesUserPrompt) {
                // 프롬프트 정보를 포함한 에이전트 데이터 업데이트
                return {
                    ...agent,
                    // 프롬프트 정보가 에이전트 객체에 포함되어 있다면 업데이트
                    ...(usesSystemPrompt && { promptId: updatedPrompt.id }),
                    ...(usesUserPrompt && { userPromptId: updatedPrompt.id }),
                };
            }
            return agent;
        };

        return {
            ...oldData,
            data: oldData.data.map(updateAgentIfUsesPrompt),
            list: oldData.list.map(updateAgentIfUsesPrompt),
        };
    });

    // 개별 에이전트 상세 캐시도 업데이트
    const queryCache = queryClient.getQueryCache();
    const agentDetailQueries = queryCache.findAll({
        predicate: query => {
            const queryKey = query.queryKey;
            return Array.isArray(queryKey) && queryKey[0] === 'agents' && queryKey[1] === 'detail';
        },
    });

    agentDetailQueries.forEach(query => {
        const currentData = query.state.data as AgentView;
        if (currentData && (currentData.promptId === promptId || currentData.userPromptId === promptId)) {
            const updatedAgent = {
                ...currentData,
                ...(currentData.promptId === promptId && { promptId: updatedPrompt.id }),
                ...(currentData.userPromptId === promptId && { userPromptId: updatedPrompt.id }),
            };
            queryClient.setQueryData(query.queryKey, updatedAgent);
        }
    });
};
